import { useNavigate } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import styled, { css } from 'styled-components';

import theme from '@styles/theme';
import { didInfoVar } from '@store/index';
import NO_IMAGE from '@assets/images/empty.png';
import { PATIENT_CHART_DATA } from '@lib/DUMMY';

import {
    FlexBox,
    RegularFont,
    SemiBoldFont
} from '@components/styled/StyledComponents';

const DidViewer = ({ data }: any) => {
    const navigate = useNavigate();
    const { version, type, didTextMsg, didBgColor, didPointColor } =
        useReactiveVar(didInfoVar);

    // 헬로미디어 타입 1.0, 3.0 화면 구성 분기 처리
    const getDidComposition = () => {
        if (version === 1) {
            switch (type) {
                case 1:
                    return (
                        <>
                            <NoticeSection />
                            <ChartSection pointColor={didPointColor} />
                            <MediaSection AD />
                        </>
                    );
                case 2:
                    return (
                        <>
                            <NoticeSection />
                            <MediaSection />
                            <MediaSection AD />
                        </>
                    );
                case 3:
                    return (
                        <>
                            <MediaSection height="66.6%" />
                            <MediaSection AD />
                        </>
                    );
            }
        } else if (version === 3) {
            return (
                <>
                    <MediaSection height="66.6%" />
                    <ChartSection pointColor={didPointColor} />
                </>
            );
        }
    };

    // 전면 이미지/동영상
    if (version === 2 || (version === 1 && type === 4)) {
        return (
            <Wrapper onClick={() => navigate(`/equipment/${data.id}/edit`)}>
                <MediaSection height="100%" />
            </Wrapper>
        );
    }

    return (
        <Wrapper onClick={() => navigate(`/equipment/${data.id}/edit`)}>
            <Content
                $bgColor={didBgColor}
                $height={
                    version === 1 && didTextMsg ? 'calc(100% - 17px)' : '100%'
                }
            >
                <Title>이지스병원</Title>
                <FlexBox
                    $flexDirection="column"
                    $gap={12}
                    $margin="8px 0 0"
                    $height="calc(100% - 32px)"
                >
                    {getDidComposition()}
                </FlexBox>
            </Content>
            {version === 1 && didTextMsg && (
                <TextBanner>
                    [이지스뉴스]디지털 시대에 맞는 효과적인 시간 관리 기법으로는
                    우선순위 설정
                </TextBanner>
            )}
        </Wrapper>
    );
};

export default DidViewer;

const MediaSection = ({
    height,
    description = '광고 영역',
    AD
}: {
    height?: string;
    description?: string;
    AD?: boolean;
}) => {
    const setImageRatio = () => {
        switch (height) {
            case '66.6%':
                return 'https://picsum.photos/285/320';
            case '100%':
                return 'https://picsum.photos/303/540';
            default:
                return 'https://picsum.photos/285/160';
        }
    };
    return (
        <BannerWrapper $type={'media'} $height={height}>
            {AD && <AdInfo>{description}</AdInfo>}
            <Image
                src={setImageRatio()}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    e.currentTarget.src = NO_IMAGE;
                }}
                alt="광고 배너"
                $filter={AD}
            />
        </BannerWrapper>
    );
};
const NoticeSection = ({ height }: { height?: string }) => {
    return (
        <BannerWrapper $type={'notice'} $height={height}>
            <Title $fontSize={10}>공지사항</Title>
            <Notice>
                이지스병원 외래진료 휴진안내입니다. 다가오는 2025년 3월
                1일(토)-삼일절 , 2025년 3월3일(일)-임시공휴일 에는 휴진이오니
                외래진료예약 및 진료에 착오없으시기 바랍니다.
            </Notice>
        </BannerWrapper>
    );
};
const ChartSection = ({
    height,
    pointColor
}: {
    height?: string;
    pointColor?: string;
}) => {
    return (
        <BannerWrapper $type={'chart'} $height={height}>
            <FlexBox $justifyContent="space-between">
                {PATIENT_CHART_DATA.map((chart, index) => (
                    <ChartWrapper key={`${index}-chart`}>
                        <FlexBox $justifyContent="space-between">
                            <Title $fontSize={10}>진료실{index + 1}</Title>
                            <PatientNum $bgColor={pointColor}>
                                {chart.length}명
                            </PatientNum>
                        </FlexBox>
                        {chart.map((patient, index) => (
                            <FlexBox
                                key={`${index}-patient`}
                                $justifyContent="space-between"
                                $margin="8px 0 0"
                                $padding="0 2px"
                                $fontColor={
                                    index ? theme.colors.blackColor : pointColor
                                }
                            >
                                <RegularFont
                                    $fontSize={10}
                                    $fontColor="inherit"
                                >
                                    {index
                                        ? `${String(index).padStart(3, '0')}번`
                                        : '진료중'}
                                </RegularFont>
                                <SemiBoldFont
                                    $fontSize={12}
                                    $fontColor="inherit"
                                >
                                    {patient}
                                </SemiBoldFont>
                            </FlexBox>
                        ))}
                    </ChartWrapper>
                ))}
            </FlexBox>
        </BannerWrapper>
    );
};

const Wrapper = styled.div`
    width: 305px;
    height: 542px;
    border: ${theme.colors.blackColor} 1px solid;
    cursor: pointer;
`;
const Content = styled.div<{ $height?: string; $bgColor?: string }>`
    padding: 10px;
    width: 100%;
    height: ${({ $height }) => $height};
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.ultraLightGrayBorder};
`;
const TextBanner = styled(FlexBox)`
    padding: 0 4px;
    width: 100%;
    height: 17px;
    color: ${theme.colors.whiteColor};
    background-color: ${theme.colors.blackColor};
    font-size: 10px;
    overflow: hidden;
`;
const Title = styled.div<{ $fontSize?: number }>`
    font-size: ${({ $fontSize }) => ($fontSize ? $fontSize : 15)}px;
    font-family: 'S-CoreDream-6Bold', sans-serif;
`;
const BannerWrapper = styled.div<{
    $flexOrder?: number;
    $type?: string;
    $height?: string;
    $borderRadius?: number;
}>`
    order: ${({ $flexOrder }) => $flexOrder};
    width: 100%;
    height: ${({ $height }) => ($height ? $height : '33.3%')};
    border-radius: 10px;
    font-size: 10px;
    font-family: PretendardMedium, sans-serif;
    overflow: hidden;

    ${({ $type }) =>
        $type === 'notice' &&
        css`
            padding: 10px 14px;
            background-color: #e8ecef;
        `}
    ${({ $type, $height }) =>
        $type === 'media' &&
        css`
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            border: ${theme.colors.blackColor} 2px solid;

            ${$height === '100%' &&
            css`
                border: none;
                border-radius: 0;
            `}
        `}
    ${({ $type }) =>
        $type === 'chart' &&
        css`
            padding: 8px 16px;
            background-color: ${theme.colors.whiteColor};
        `};
`;
const Notice = styled.div`
    margin: 4px 0 0;
    white-space: pre-wrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3; /* 3줄 넘어가면 말줄임 */
    -webkit-box-orient: vertical;
`;
const Image = styled.img<{ $filter?: boolean }>`
    width: 100%;
    object-fit: contain;
    ${({ $filter }) =>
        $filter &&
        css`
            filter: brightness(60%);
        `}
`;
const AdInfo = styled.span`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${theme.colors.whiteColor};
    font-size: 18px;
    font-family: PretendardMedium, sans-serif;
    z-index: 10;
`;
const ChartWrapper = styled.div`
    width: 70px;
`;
const PatientNum = styled.div<{ $bgColor?: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px;
    width: 30px;
    height: 20px;
    font-size: 10px;
    color: ${theme.colors.whiteColor};
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.blackColor};
    border-radius: 4px;
`;
